import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useCardForm } from "src/hooks/useCardForm";
import { useMcpCache } from "src/hooks/useMcpCache";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { VitalSignsFormData, VitalSignsCreateProps, AlertState } from "../model";
import { checkHasKoah, clamp, digitsClamp } from "../utils";
import { VitalSignsFormHeader } from "../components/VitalSignsFormHeader";
import { VitalSignsDateTimeSection } from "../components/VitalSignsDateTimeSection";
import { VitalSignsParameterRow } from "../components/VitalSignsParameterRow";
import { VitalSignsO2Section } from "../components/VitalSignsO2Section";

export default function VitalSignsCreate({
    recordId,
    patientId,
    formData: parentFormData,
    onChange: parentOnChange,
    patientData,
    extraAccess,
    save,
    isSaving,
}: VitalSignsCreateProps) {
    const { t } = useTranslate();
    const { user } = useAppSelector((state) => state.auth);

    const [alert, setAlert] = useState<AlertState>({
        open: false,
        message: "",
        type: "success",
    });

    const showAlert = (msg: string, type: "success" | "error") => {
        setAlert({
            open: true,
            message: msg,
            type,
        });
    };

    const initialSnapshotRef = useRef<any>(null);

    const editPrefillDoneRef = useRef<number | null>(null);
    const editMcpPrefillDoneRef = useRef<number | null>(null);
    const isPrefillingMcpRef = useRef(false);
    const ewsInputsChangedRef = useRef(false);
    const {
        formData,
        selectedMCPs,
        loading,
        handleChange,
        handleMCPChange,
        cardData,
        isEditMode,
        getMCPParameterDetails,
        mcpCacheLoaded,
    } = useCardForm({
        cardType: "VITAL_SIGNS",
        recordId: recordId ? String(recordId) : undefined,
        user,
    });

    const typedFormData = formData as VitalSignsFormData;

    const VITAL_FIELDS = useMemo(() => [
        "BODY_TEMPERATURE", "MEAS_METHOD_OF_BODY_TEMP", "HEART_RATE", "HEART_RATE_TYPE",
        "SYS", "DIA", "RESPIRATORY_COUNT", "RESPIRATORY_COUNT_TYPE", "RESPIRATORY_DISTRESS",
        "O2_SATURUZATION", "IS_TAKEN_O2", "O2_AMOUNT", "O2_UNIT", "LEVEL_OF_CONSCIOUSNESS",
        "CAPILLARY_CIRCULATION", "PAIN_LEVEL", "DEFAKASYON", "URINE", "WRITE_ON_EPICRISE",
        "SIYONEZE_CIRCULATION_DISORDER", "KOAH_SATURATION", "BIRTH_PAIN", "BIRTH_LOSIA",
        "BIRTH_PROTEINURIA", "EVAULATION", "PTIME"
    ], []);

    const checkValidations = useCallback(() => {
        if (!initialSnapshotRef.current) return true;

        let isDirty = false;
        for (const key of VITAL_FIELDS) {
            const cur = (typedFormData as any)[key];
            const init = initialSnapshotRef.current[key];

            if (cur instanceof Date && init instanceof Date) {
                if (cur.getTime() !== init.getTime()) {
                    isDirty = true;
                    break;
                }
            } else {
                const normalizedCur = (cur === null || cur === undefined || cur === 0 || cur === "0") ? "" : String(cur);
                const normalizedInit = (init === null || init === undefined || init === 0 || init === "0") ? "" : String(init);
                if (normalizedCur !== normalizedInit) {
                    isDirty = true;
                    break;
                }
            }
        }

        if (!isDirty) {
            showAlert(t("no-changes-to-save"), "error");
            return false;
        }

        return true;
    }, [typedFormData, VITAL_FIELDS, t]);

    useEffect(() => {
        if (loading || initialSnapshotRef.current) return;

        if (isEditMode) {
            if (editMcpPrefillDoneRef.current === Number(recordId)) {
                initialSnapshotRef.current = { ...formData };
            }
        } else {
            if (mcpCacheLoaded) {
                initialSnapshotRef.current = { ...formData };
            }
        }
    }, [loading, isEditMode, formData, recordId, mcpCacheLoaded]);

    const patientBirthDate = useMemo(() => {
        const raw = patientData?.birthdate;
        if (!raw) return null;
        const d = new Date(raw);
        return Number.isFinite(d.getTime()) ? d : null;
    }, [patientData]);

    const isInfant = useMemo(() => {
        if (!patientBirthDate) return false;
        const diffMs = Date.now() - patientBirthDate.getTime();
        return diffMs >= 0 && diffMs < 365 * 24 * 60 * 60 * 1000;
    }, [patientBirthDate]);

    const ptyId = Number(patientData?.ptyid ?? patientData?.PTY_ID ?? NaN);
    const showEarlyWarningScore = ![1, 3].includes(ptyId);
    const isPregnantOrLactation = String(patientData?.pregnantorlactation ?? "").toUpperCase() === "D";

    const hasKoah = useMemo(() => checkHasKoah(patientData), [patientData]);

    const shouldLoadInfantConsciousnessMcp = isInfant;
    const { cacheLoaded: infantMcpCacheLoaded } = useMcpCache(
        shouldLoadInfantConsciousnessMcp ? [748] : []
    );

    const combinedMcpCacheLoaded = mcpCacheLoaded && infantMcpCacheLoaded;

    const getMcpId = useCallback((fieldName: string) =>
        cardData?.mcpConfig?.[fieldName]?.mcpId || null, [cardData]);

    const ewsMcpFields = useMemo(
        () => {
            const fields: Array<keyof VitalSignsFormData> = [
                "HEART_RATE_TYPE",
                "RESPIRATORY_COUNT_TYPE",
                "RESPIRATORY_DISTRESS",
                "SIYONEZE_CIRCULATION_DISORDER",
                "IS_TAKEN_O2",
                "LEVEL_OF_CONSCIOUSNESS",
            ];
            if (isInfant) {
                fields.push("CAPILLARY_CIRCULATION");
            }
            return fields;
        },
        [isInfant]
    );

    const isEwsField = useCallback(
        (fieldName: keyof VitalSignsFormData) => ewsMcpFields.includes(fieldName),
        [ewsMcpFields]
    );

    const applyMcpChange = useCallback((
        fieldName: keyof VitalSignsFormData,
        value: any
    ) => {
        if (!isPrefillingMcpRef.current && isEwsField(fieldName)) {
            ewsInputsChangedRef.current = true;
        }
        handleMCPChange(fieldName as string, value);
    }, [handleMCPChange, isEwsField]);

    useEffect(() => {
        if (!isEditMode || !recordId) return;
        if (!parentFormData) return;
        if (!combinedMcpCacheLoaded) return;

        const parentId = Number(parentFormData?.id ?? parentFormData?.ID ?? NaN);
        if (!Number.isFinite(parentId)) return;
        if (parentId !== Number(recordId)) return;

        if (editPrefillDoneRef.current !== parentId) {
            handleChange("ID", parentId);
            handleChange("PT_ID", parentFormData?.pt_id ?? parentFormData?.PT_ID ?? patientId ?? null);

            handleChange(
                "PTIME",
                (parentFormData?.ptime || parentFormData?.PTIME) ? new Date((parentFormData.ptime || parentFormData.PTIME) as string) : null
            );

            handleChange("BODY_TEMPERATURE", parentFormData?.body_temperature ?? parentFormData?.BODY_TEMPERATURE ?? "");
            handleChange("HEART_RATE", parentFormData?.heart_rate ?? parentFormData?.HEART_RATE ?? "");
            handleChange("SYS", parentFormData?.sys ?? parentFormData?.SYS ?? "");
            handleChange("DIA", parentFormData?.dia ?? parentFormData?.DIA ?? "");
            handleChange("RESPIRATORY_COUNT", parentFormData?.respiratory_count ?? parentFormData?.RESPIRATORY_COUNT ?? "");
            handleChange("O2_AMOUNT", parentFormData?.o2_amount ?? parentFormData?.O2_AMOUNT ?? "");
            handleChange("EARLY_WARNING_SCORE", parentFormData?.early_warning_score ?? parentFormData?.EARLY_WARNING_SCORE ?? "");
            handleChange("WRITE_ON_EPICRISE", parentFormData?.write_on_epicrise ?? parentFormData?.WRITE_ON_EPICRISE ?? "0");
            handleChange("O2_SATURUZATION", parentFormData?.o2_saturuzation ?? parentFormData?.O2_SATURUZATION ?? "");
            handleChange("CUSER", parentFormData?.cuser ?? parentFormData?.CUSER ?? "");

            const applyMcpSelection = (
                fieldName: keyof VitalSignsFormData,
                val: any,
                overrideMcpId?: number
            ) => {
                const mcpId = overrideMcpId ?? getMcpId(fieldName as string);
                if (!mcpId) return;

                if (val === null || val === undefined || val === 0 || val === "0" || val === "") {
                    applyMcpChange(fieldName, null);
                    return;
                }

                const mcpDetails = getMCPParameterDetails(mcpId);
                const foundDetail = mcpDetails.find((d: any) => String(d.id) === String(val));

                applyMcpChange(fieldName, {
                    id: String(val),
                    mcpId,
                    name: foundDetail?.name || "",
                    note: null,
                    value: foundDetail?.value ?? 0,
                    orderNumber: null,
                } as any);
            };

            isPrefillingMcpRef.current = true;
            try {
                applyMcpSelection("MEAS_METHOD_OF_BODY_TEMP", parentFormData?.meas_method_of_body_temp ?? parentFormData?.MEAS_METHOD_OF_BODY_TEMP);
                applyMcpSelection("HEART_RATE_TYPE", parentFormData?.heart_rate_type ?? parentFormData?.HEART_RATE_TYPE);
                applyMcpSelection("RESPIRATORY_COUNT_TYPE", parentFormData?.respiratory_count_type ?? parentFormData?.RESPIRATORY_COUNT_TYPE);
                applyMcpSelection("RESPIRATORY_DISTRESS", parentFormData?.respiratory_distress ?? parentFormData?.RESPIRATORY_DISTRESS);
                applyMcpSelection(
                    "SIYONEZE_CIRCULATION_DISORDER",
                    parentFormData?.siyoneze_circulation_disorder ?? parentFormData?.SIYONEZE_CIRCULATION_DISORDER
                );
                applyMcpSelection("IS_TAKEN_O2", parentFormData?.is_taken_o2 ?? parentFormData?.IS_TAKEN_O2);
                applyMcpSelection("O2_UNIT", parentFormData?.o2_unit ?? parentFormData?.O2_UNIT);
                applyMcpSelection(
                    "LEVEL_OF_CONSCIOUSNESS",
                    parentFormData?.level_of_consciousness ?? parentFormData?.LEVEL_OF_CONSCIOUSNESS,
                    isInfant ? 748 : undefined
                );
                applyMcpSelection("PAIN_LEVEL", parentFormData?.pain_level ?? parentFormData?.PAIN_LEVEL);
                applyMcpSelection("DEFAKASYON", parentFormData?.defakasyon ?? parentFormData?.DEFAKASYON);
                applyMcpSelection("URINE", parentFormData?.urine ?? parentFormData?.URINE);
                applyMcpSelection("CAPILLARY_CIRCULATION", parentFormData?.capillary_circulation ?? parentFormData?.CAPILLARY_CIRCULATION);
                applyMcpSelection("BIRTH_PAIN", parentFormData?.birth_pain ?? parentFormData?.BIRTH_PAIN);
                applyMcpSelection("BIRTH_LOSIA", parentFormData?.birth_losia ?? parentFormData?.BIRTH_LOSIA);
                applyMcpSelection("BIRTH_PROTEINURIA", parentFormData?.birth_proteinuria ?? parentFormData?.BIRTH_PROTEINURIA);
                applyMcpSelection("KOAH_SATURATION", parentFormData?.koah_saturation ?? parentFormData?.KOAH_SATURATION);
                applyMcpSelection("EVAULATION", parentFormData?.evaulation ?? parentFormData?.EVAULATION);
            } finally {
                isPrefillingMcpRef.current = false;
            }

            editPrefillDoneRef.current = parentId;
            editMcpPrefillDoneRef.current = parentId;
            ewsInputsChangedRef.current = false;
        }
    }, [
        isEditMode,
        recordId,
        parentFormData,
        patientId,
        getMCPParameterDetails,
        combinedMcpCacheLoaded,
        isInfant,
        getMcpId,
        applyMcpChange,
        handleChange,
    ]);

    useEffect(() => {
        if (parentOnChange) {
            parentOnChange(typedFormData);
        }
    }, [formData, parentOnChange, typedFormData]);

    const ewsScore = useMemo(() => {
        return ewsMcpFields.reduce((sum, field) => {
            const v = (selectedMCPs as any)?.[field as string]?.value;
            const n = Number(v);
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
    }, [ewsMcpFields, selectedMCPs]);

    const lastEwsSetRef = useRef<string | null>(null);

    useEffect(() => {
        if (isEditMode && !ewsInputsChangedRef.current) {
            return;
        }
        const next = String(ewsScore ?? 0);
        if (lastEwsSetRef.current === next) return;
        lastEwsSetRef.current = next;
        handleChange("EARLY_WARNING_SCORE", next);
    }, [ewsScore, handleChange, isEditMode]);

    const currentOrder = useMemo(() => {
        if (isInfant) {
            return [
                "PTIME", "CUSER", "EVAULATION", "HEART_RATE", "RESPIRATORY_COUNT",
                "RESPIRATORY_DISTRESS", "BLOOD_PRESSURE", "O2_SATURUZATION", "O2_SECTION",
                "LEVEL_OF_CONSCIOUSNESS", "CAPILLARY_CIRCULATION", "EARLY_WARNING_SCORE",
                "PAIN_LEVEL", "BODY_TEMPERATURE", "DEFAKASYON", "URINE",
                "WRITE_ON_EPICRISE", "SIYONEZE_CIRCULATION_DISORDER",
            ];
        }
        return [
            "PTIME", "CUSER", "BODY_TEMPERATURE", "EVAULATION", "BLOOD_PRESSURE",
            "HEART_RATE", "RESPIRATORY_COUNT", "O2_SATURUZATION", "O2_SECTION",
            "LEVEL_OF_CONSCIOUSNESS", "EARLY_WARNING_SCORE", "RESPIRATORY_DISTRESS",
            "PAIN_LEVEL", "DEFAKASYON", "URINE", "WRITE_ON_EPICRISE",
            "SIYONEZE_CIRCULATION_DISORDER",
        ];
    }, [isInfant]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const renderMcpSelect = (
        fieldName: keyof VitalSignsFormData,
        mcpConfigKey: string = fieldName as string,
        width: string = "100%",
        overrideMcpId?: number
    ) => {
        const mcpId = overrideMcpId ?? getMcpId(mcpConfigKey);
        if (!mcpId) return null;

        return (
            <ByzMcParameterDetailSelect
                mcpId={mcpId}
                multiple={false}
                value={selectedMCPs[fieldName as string] || null}
                handleChange={(val) => {
                    if (!Array.isArray(val)) {
                        applyMcpChange(fieldName, val);
                    }
                }}
                cachedData={getMCPParameterDetails(mcpId)}
                cacheLoaded={combinedMcpCacheLoaded}
                sx={{ width }}
                title={t("select")}
                readOnly={!extraAccess}
            />
        );
    };

    const renderField = (fieldKey: string) => {
        switch (fieldKey) {
            case "PTIME":
                return (
                    <VitalSignsDateTimeSection
                        key="PTIME"
                        value={typedFormData.PTIME || null}
                        onChange={(v) => handleChange("PTIME", v)}
                        disabled={!extraAccess}
                    />
                );
            case "CUSER":
                return (
                    <VitalSignsParameterRow key="CUSER" label1={t("processuser")}>
                        <Grid xs={12}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                color="info"
                                variant="outlined"
                                value={typedFormData.CUSER || ""}
                                onChange={(e) => handleChange("CUSER", e.target.value)}
                                label={t("processuser")}
                                disabled
                            />
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "BODY_TEMPERATURE":
                return (
                    <VitalSignsParameterRow
                        key="BODY_TEMPERATURE"
                        label1={t("vital-signs.body-temperature")}
                        label2={t("vital-signs.measurement-method")}
                    >
                        <Grid xs={12} sm={6}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.BODY_TEMPERATURE || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "BODY_TEMPERATURE",
                                        clamp(e.target.value, 4)
                                    )
                                }
                                type="number"
                                inputProps={{ maxLength: 4 }}
                                placeholder={t("enter") ?? "Giriniz"}
                                disabled={!extraAccess}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            {renderMcpSelect("MEAS_METHOD_OF_BODY_TEMP")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "EVAULATION":
                return (
                    <VitalSignsParameterRow key="EVAULATION" label1={t("vital-signs.evaluation")}>
                        <Grid xs={12}>
                            {renderMcpSelect("EVAULATION")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "HEART_RATE":
                return (
                    <VitalSignsParameterRow
                        key="HEART_RATE"
                        label1={t("vital-signs.heart-rate")}
                        label2={t("vital-signs.heart-rate-type")}
                    >
                        <Grid xs={12} sm={6}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.HEART_RATE || ""}
                                onChange={(e) =>
                                    handleChange("HEART_RATE", clamp(e.target.value, 3))
                                }
                                inputProps={{ maxLength: 3 }}
                                disabled={!extraAccess}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            {renderMcpSelect("HEART_RATE_TYPE")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "RESPIRATORY_COUNT":
                return (
                    <VitalSignsParameterRow
                        key="RESPIRATORY_COUNT"
                        label1={t("vital-signs.respiratory-rate")}
                        label2={t("vital-signs.respiratory-type")}
                    >
                        <Grid xs={12} sm={6}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.RESPIRATORY_COUNT || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "RESPIRATORY_COUNT",
                                        clamp(e.target.value, 3)
                                    )
                                }
                                inputProps={{ maxLength: 3 }}
                                disabled={!extraAccess}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            {renderMcpSelect("RESPIRATORY_COUNT_TYPE")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "RESPIRATORY_DISTRESS":
                return (
                    <VitalSignsParameterRow key="RESPIRATORY_DISTRESS" label1={t("vital-signs.shortness-of-breath")}>
                        <Grid xs={12}>
                            {renderMcpSelect("RESPIRATORY_DISTRESS")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "BLOOD_PRESSURE":
                return (
                    <VitalSignsParameterRow
                        key="BLOOD_PRESSURE"
                        label1={t("vital-signs.sys")}
                        label2={t("vital-signs.dia")}
                    >
                        <Grid xs={12} sm={6}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.SYS || ""}
                                onChange={(e) => handleChange("SYS", clamp(e.target.value, 3))}
                                inputProps={{ maxLength: 3 }}
                                disabled={!extraAccess}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.DIA || ""}
                                onChange={(e) => handleChange("DIA", clamp(e.target.value, 3))}
                                inputProps={{ maxLength: 3 }}
                                disabled={!extraAccess}
                            />
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "O2_SATURUZATION":
                return (
                    <VitalSignsParameterRow key="O2_SATURUZATION" label1={t("vital-signs.o2-saturation")}>
                        <Grid xs={12}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.O2_SATURUZATION || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "O2_SATURUZATION",
                                        digitsClamp(e.target.value, 3)
                                    )
                                }
                                type="text"
                                inputProps={{ maxLength: 3, inputMode: "numeric", pattern: "[0-9]*" }}
                                disabled={!extraAccess}
                            />
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "O2_SECTION":
                return (
                    <VitalSignsO2Section key="O2_SECTION">
                        <Grid xs={12} sm={4}>
                            {renderMcpSelect("IS_TAKEN_O2")}
                        </Grid>
                        <Grid xs={12} sm={4}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.O2_AMOUNT || ""}
                                onChange={(e) =>
                                    handleChange("O2_AMOUNT", clamp(e.target.value, 2))
                                }
                                inputProps={{ maxLength: 2 }}
                                disabled={!extraAccess}
                                label={t("vital-signs.o2-amount")}
                            />
                        </Grid>
                        <Grid xs={12} sm={4}>
                            {renderMcpSelect("O2_UNIT")}
                        </Grid>
                    </VitalSignsO2Section>
                );
            case "LEVEL_OF_CONSCIOUSNESS":
                return (
                    <VitalSignsParameterRow key="LEVEL_OF_CONSCIOUSNESS" label1={t("vital-signs.level-of-consciousness")}>
                        <Grid xs={12}>
                            {renderMcpSelect(
                                "LEVEL_OF_CONSCIOUSNESS",
                                "LEVEL_OF_CONSCIOUSNESS",
                                "100%",
                                isInfant ? 748 : undefined
                            )}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "EARLY_WARNING_SCORE":
                return showEarlyWarningScore ? (
                    <VitalSignsParameterRow key="EARLY_WARNING_SCORE" label1={t("vital-signs.early-warning-score")}>
                        <Grid xs={12}>
                            <ByzMuiTextField
                                sx={{ width: "100%" }}
                                value={typedFormData.EARLY_WARNING_SCORE || ""}
                                label={t("vital-signs.early-warning-score")}
                                placeholder=""
                                disabled
                            />
                        </Grid>
                    </VitalSignsParameterRow>
                ) : null;
            case "CAPILLARY_CIRCULATION":
                return isInfant ? (
                    <VitalSignsParameterRow key="CAPILLARY_CIRCULATION" label1={t("vital-signs.capillary-circulation")}>
                        <Grid xs={12}>
                            {renderMcpSelect("CAPILLARY_CIRCULATION")}
                        </Grid>
                    </VitalSignsParameterRow>
                ) : null;
            case "PAIN_LEVEL":
                return (
                    <VitalSignsParameterRow key="PAIN_LEVEL" label1={t("vital-signs.pain-status")}>
                        <Grid xs={12}>
                            {renderMcpSelect("PAIN_LEVEL")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "DEFAKASYON":
                return (
                    <VitalSignsParameterRow key="DEFAKASYON" label1={t("vital-signs.defecation")}>
                        <Grid xs={12}>
                            {renderMcpSelect("DEFAKASYON")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "URINE":
                return (
                    <VitalSignsParameterRow key="URINE" label1={t("vital-signs.urine")}>
                        <Grid xs={12}>
                            {renderMcpSelect("URINE")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "WRITE_ON_EPICRISE":
                return (
                    <VitalSignsParameterRow key="WRITE_ON_EPICRISE" label1={t("vital-signs.come-out-in-epicrisis")}>
                        <Grid xs={12}>
                            <ByzMuiCheckBox
                                checked={typedFormData.WRITE_ON_EPICRISE === "1"}
                                onChange={(e) => handleChange("WRITE_ON_EPICRISE", e.target.checked ? "1" : "0")}
                                disabled={!extraAccess}
                            />
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "SIYONEZE_CIRCULATION_DISORDER":
                return (
                    <VitalSignsParameterRow key="SIYONEZE_CIRCULATION_DISORDER" label1={t("vital-signs.cyanosis-circulatory-disorder")}>
                        <Grid xs={12}>
                            {renderMcpSelect("SIYONEZE_CIRCULATION_DISORDER")}
                        </Grid>
                    </VitalSignsParameterRow>
                );
            case "KOAH_SATURATION":
                return hasKoah ? (
                    <VitalSignsParameterRow key="KOAH_SATURATION" label1={t("vital-signs.koah-saturation")}>
                        <Grid xs={12}>
                            {renderMcpSelect("KOAH_SATURATION")}
                        </Grid>
                    </VitalSignsParameterRow>
                ) : null;
            case "PREGNANCY_FIELDS":
                return isPregnantOrLactation ? (
                    <Box key="PREGNANCY_FIELDS" sx={{ display: 'contents' }}>
                        <VitalSignsParameterRow label1={t("vital-signs.birth-proteinuria")}>
                            <Grid xs={12}>
                                {renderMcpSelect("BIRTH_PROTEINURIA")}
                            </Grid>
                        </VitalSignsParameterRow>
                        <VitalSignsParameterRow label1={t("vital-signs.birth-pain")}>
                            <Grid xs={12}>
                                {renderMcpSelect("BIRTH_PAIN")}
                            </Grid>
                        </VitalSignsParameterRow>
                        <VitalSignsParameterRow label1={t("vital-signs.birth-losia")}>
                            <Grid xs={12}>
                                {renderMcpSelect("BIRTH_LOSIA")}
                            </Grid>
                        </VitalSignsParameterRow>
                    </Box>
                ) : null;
            default:
                return null;
        }
    };

    const extraFields = ["KOAH_SATURATION", "PREGNANCY_FIELDS"];

    return (
        <Box sx={{ padding: "20px", width: "100%" }}>
            {save && (
                <Box mb={3}>
                    <ByzMuiSaveButton
                        onClick={() => {
                            if (!checkValidations()) return;
                            save(String(typedFormData.ID || ""));
                        }}
                        loading={isSaving}
                        disabled={!extraAccess}
                        tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
                    />
                </Box>
            )}

            <Grid container spacing={2}>
                <Grid
                    xs={12}
                    md={12}
                    container
                    direction="column"
                    spacing={3}
                >
                    <VitalSignsFormHeader title={t("vital-signs.title")} />

                    {currentOrder.map(renderField)}
                    {extraFields.map(renderField)}

                </Grid>
            </Grid>

            <ByzMuiSnackBar
                open={alert.open}
                onClose={() => setAlert(prev => ({ ...prev, open: false }))}
                title={alert.type === "success" ? t("success") : t("error")}
                message={alert.message}
                type={alert.type}
            />
        </Box>
    );
}
